import ApplicationController from "./application_controller"

// Scroll distance (px) before the fixed sticky nav bar slides in.
const FIXED_NAV_SHOW_SCROLL_PX = 250;
// Fraction of viewport height at which a section is "active" when scrolling.
const ACTIVE_SECTION_THRESHOLD_RATIO = 0.25;
// Extra breathing room so a section header isn't flush against the sticky bar after jump.
const SCROLL_BREATHING_ROOM = 8;
// Ghost tooltip positioning constants.
const GHOST_TOOLTIP_VIEWPORT_PADDING = 10;
const GHOST_TOOLTIP_OFFSET           = 10;
// Stable JS hook for the main search results card (excludes LexiQamus ENG definition shell).
const SEARCH_RESULTS_CARD_SELECTOR = '.js-search-results-card';
// Fallbacks only when data-section-initial-rows / data-section-reveal-batch are missing.
const SECTION_INITIAL_VISIBLE_FALLBACK = 5;
const SECTION_REVEAL_BATCH_FALLBACK = 25;
// Content height must exceed one line by this factor to count as wrapped (tolerates line-height rounding).
const WORD_BOX_MULTILINE_HEIGHT_RATIO = 1.5;

// postMessage 'type' sent by the LexiQamus ENG definition embed to report iframe content height.
// This is flag passed by the lexiqamus site to set the iframe content height.
const BUILDLQ_EMBED_RESIZE_MESSAGE_TYPE = "buildlq-embed-resize"

// Used to reset the definition iframe to a blank state (no content, no pending network request).
// Setting src to about:blank is the standard way to immediately stop any in-flight load and clear
// the iframe's document without triggering a new HTTP request or leaving stale content visible.
const BLANK_IFRAME_SRC = "about:blank"

export default class extends ApplicationController {
  static targets = [
    'fuzzyCtaButton',
    'fuzzySuggestionsList',
    'fuzzyExpandButton',
    'subscriptionRecommendationBesideFuzzyCta',
    'subscriptionRecommendationBelowFuzzyList',
    'resultsTable',
    'dictionaryColumnHeaderLabel',
    'definitionIframeContainer',
    'definitionIframe',
    'definitionSearchLoadingIndicator'
  ] //Cta stands for "Call to Action" button.
  static values = {
    failedToLoadContext: String,
    copied: String,
    toggleUrl: String,
    definitionSearchBaseUrl: String,
    // Localized labels injected from the view for header count text.
    recordsFoundSingular: String,
    recordsFoundPlural: String,
    overallRecordsCount: Number,
    spellingTotals: Object
  }

  // Gets called in initialize()
  #assignDefaultValues() {
    this.definitionIframeDefaultHeight = 800
    this.definitionSearchLoadingFallbackTimer = null
    this.nestedTooltipListenersAttached = false
  }

  initialize() {
    this.#assignDefaultValues()
    this.onMainTabShown = this.onMainTabShown.bind(this);
    this.boundNestedTooltipMouseOver = (event) => this.handleNestedTooltipMouseOver(event, this.nestedTooltipParentSelector);
    this.boundNestedTooltipMouseOut  = (event) => this.handleNestedTooltipMouseOut(event, this.nestedTooltipParentSelector);
    // Bound for window "message" listener: resizes ENG definition iframe when embed sends postMessage height.
    this.boundHandleDefinitionIframeResize = this.handleDefinitionIframeResize.bind(this);
    // Bound for iframe "load" event: starts fallback timer in case the embed never posts its resize message.
    this.boundHandleDefinitionIframeLoad = this.handleDefinitionIframeLoad.bind(this);
    this.isDiscoverInnovationsScrollEnable = false
  }

  connect() {
    window['searchOutput'] = this;
    // this.showFirstTableByDefault();  // TODO: This code AI generated code; it may not be needed. Remove this method once confirmed.
    this.setupMainTabActivation();
    this.setupCitationModal();
       // this.setupUrlHashSupport(); // TODO: This code AI generated code; it may not be needed. Remove this method once confirmed. 
    this.boundHandleTabResultsLoaded = this.handleTabResultsLoaded.bind(this);
    document.addEventListener('searchOutputTabResultsLoaded', this.boundHandleTabResultsLoaded);
    this.boundActivateSearchTabFromParams = this.activateSearchTabFromParams.bind(this);
    document.addEventListener('searchOutputTabResultsLoaded', this.boundActivateSearchTabFromParams, { once: true });
    this.boundRefreshWordBoxes = this.refreshWordBoxes.bind(this);
    window.addEventListener('resize', this.boundRefreshWordBoxes);
    this.refreshWordBoxes();
    this.syncDictionaryColumnAlignmentOffset();
    this.syncResultsStickyHeaderColumnWidths();
    this.syncFixedNavWidth();
    this.initializeResultTooltips();
    this.initializeResultsGhostTooltips();
    // Cache static DOM references used on every scroll/resize tick.
    this._cachedJumpLinks       = Array.from(this.element.querySelectorAll('.js-results-jump-link'));
    this._cachedResultsFixedNav = document.getElementById('resultsFixedStickyNav');
    this._cachedResultsLayouts  = Array.from(this.element.querySelectorAll('.js-results-list-layout'));
    // Throttle scroll handler with rAF to avoid layout thrashing on every scroll tick.
    this._jumpLinksRafPending = false;
    this.boundUpdateResultsJumpLinks = () => {
      if (this._jumpLinksRafPending) return;
      this._jumpLinksRafPending = true;
      requestAnimationFrame(() => {
        this._jumpLinksRafPending = false;
        this.updateResultsJumpLinks();
      });
    };
    window.addEventListener('scroll', this.boundUpdateResultsJumpLinks, { passive: true });
    this.updateResultsJumpLinks();
    this.setupColumnHeaderStickyObserver();
    // "message" is the browser event for window.postMessage (not UI/error messages).This is not normal codebase approach but it is the only way to get the height of the iframe content.
    window.addEventListener("message", this.boundHandleDefinitionIframeResize);
    this.lockLandingPageScroll();
  }

  disconnect() {
    window['searchOutput'] = undefined;
    document.removeEventListener('searchOutputTabResultsLoaded', this.boundHandleTabResultsLoaded);
    document.removeEventListener('searchOutputTabResultsLoaded', this.boundActivateSearchTabFromParams);
    window.removeEventListener('resize', this.boundRefreshWordBoxes);
    window.removeEventListener('scroll', this.boundUpdateResultsJumpLinks);
    this.teardownColumnHeaderStickyObserver();
    this.teardownResultsGhostTooltips();

    // "message" is the browser event for window.postMessage (not UI/error messages).This is not normal codebase approach but it is the only way to get the height of the iframe content.
    window.removeEventListener("message", this.boundHandleDefinitionIframeResize);
    if (this.definitionSearchLoadingFallbackTimer) {
      clearTimeout(this.definitionSearchLoadingFallbackTimer)
      this.definitionSearchLoadingFallbackTimer = null
    }

    this.teardownNestedTooltipListeners();
    this._cachedJumpLinks       = null;
    this._cachedResultsFixedNav = null;
    this._cachedResultsLayouts  = null;
  }

  // Result column header: JS-managed `position: fixed` under the Jump To bar (sticky
  // breaks inside .tab-pane/.results-list-layout). A sentinel at the header's natural
  // position toggles .is-attached/.is-detached on scroll; when attached, the sentinel
  // reserves height to avoid layout jump. Left/width follow .js-results-list-layout;
  // column widths sync from the live body table.
  //
  setupColumnHeaderStickyObserver() {
    this.teardownColumnHeaderStickyObserver();

    const sentinel = this.element.querySelector('.js-results-column-header-sentinel');
    const header   = this.element.querySelector('.js-results-column-header');
    if (!sentinel || !header) return;

    const jumpToNav = document.getElementById('resultsFixedStickyNav');

    // The layout container — used to derive left + width for the fixed header.
    const layoutEl = this.element.querySelector('.js-results-list-layout') || this.element;

    // Position and size the fixed header to align with the layout container.
    const syncFixedPosition = () => {
      const rect = layoutEl.getBoundingClientRect();
      const navH = this.visibleJumpToNavHeight(jumpToNav);
      header.style.setProperty("--results-sticky-header-left", `${Math.round(rect.left)}px`);
      header.style.setProperty("--results-sticky-header-width", `${Math.round(rect.width)}px`);
      header.style.setProperty("--results-jump-nav-offset", `${Math.round(navH)}px`);
    };

    // Resize handling — re-sync position and column widths.
    this._boundSyncOnResize = () => {
      requestAnimationFrame(() => {
        syncFixedPosition();
        this.syncDictionaryColumnAlignmentOffset();
        this.syncFixedNavWidth();
        this.syncResultsStickyHeaderColumnWidths();
        this._tickStickyHeader(sentinel, header, jumpToNav, syncFixedPosition);
        this.refreshWordBoxes();
      });
    };
    window.addEventListener('resize', this._boundSyncOnResize, { passive: true });

    // ResizeObserver on the Jump To nav for font-scale / content-driven height changes.
    if (jumpToNav && typeof ResizeObserver !== 'undefined') {
      this.jumpToNavResizeObserver = new ResizeObserver(() => {
        syncFixedPosition();
        this._tickStickyHeader(sentinel, header, jumpToNav, syncFixedPosition);
      });
      this.jumpToNavResizeObserver.observe(jumpToNav);
    }

    // Run once immediately so initial state is correct without waiting for a scroll.
    syncFixedPosition();
    this._tickStickyHeader(sentinel, header, jumpToNav, syncFixedPosition);

    // Store refs for _tickStickyHeader calls from updateResultsJumpLinks.
    this._stickyHeaderRefs = { sentinel, header, jumpToNav, syncFixedPosition };
  }

  visibleJumpToNavHeight(jumpToNav) {
    if (!jumpToNav || !jumpToNav.classList.contains('is-visible')) return 0
    return jumpToNav.getBoundingClientRect().height
  }

  // Single tick: decides whether the column header should be pinned (fixed) or detached (in-flow).
  _tickStickyHeader(sentinel, header, jumpToNav, syncFixedPosition) {
    const navBottom   = this.visibleJumpToNavHeight(jumpToNav);
    const sentinelTop = sentinel.getBoundingClientRect().top;
    const shouldPin   = sentinelTop <= navBottom;

    const isPinned = header.classList.contains('is-attached');
    if (shouldPin === isPinned) return; // no change

    if (shouldPin) {
      // Measure the header's in-flow height before it leaves the flow.
      const headerHeight = header.getBoundingClientRect().height;
      // Set sentinel height first so layout doesn't shift when header goes fixed.
      sentinel.style.height = `${headerHeight}px`;
      syncFixedPosition();
      this.syncResultsStickyHeaderColumnWidths();
      header.classList.add('is-attached');
      header.classList.remove('is-detached');
    } else {
      // Header returns to in-flow — sentinel no longer needs to hold space.
      header.classList.remove('is-attached');
      header.classList.add('is-detached');
      sentinel.style.height = '0px';
    }
  }

  teardownColumnHeaderStickyObserver() {
    if (this._boundSyncOnResize) {
      window.removeEventListener('resize', this._boundSyncOnResize);
      this._boundSyncOnResize = null;
    }
    if (this.jumpToNavResizeObserver) {
      this.jumpToNavResizeObserver.disconnect();
      this.jumpToNavResizeObserver = null;
    }
    this._stickyHeaderRefs = null;
  }

  toggleResultsSection(event) {
    event.preventDefault();

    const toggleButton = event.currentTarget;
    const sectionId = toggleButton.dataset.resultsSectionId;
    if (!sectionId) return;

    const sectionContent = this.element.querySelector(`[data-results-section-content-for="${sectionId}"]`);
    if (!sectionContent) return;

    const isCollapsed = sectionContent.classList.contains('is-collapsed');
    if (isCollapsed) {
      sectionContent.classList.remove('is-collapsed');
      toggleButton.classList.remove('is-collapsed');
      // Reopening restores the initial Show more state (first 5 rows + capsule).
      this.resetSectionShowMore(sectionContent);
      requestAnimationFrame(() => {
        this.syncResultsStickyHeaderColumnWidths();
        this.syncFixedNavWidth();
      });
      return;
    }

    sectionContent.classList.add('is-collapsed');
    toggleButton.classList.add('is-collapsed');
    requestAnimationFrame(() => {
      this.syncResultsStickyHeaderColumnWidths();
      this.syncFixedNavWidth();
    });
  }

  // Loads the next batch of rows for an Exact Match layout section via GET section_results.
  // Deferred with setTimeout so the handler runs after the controller is fully ready (prod race).
  revealMoreSectionResults(event) {
    event.preventDefault();
    const button = event.currentTarget;

    setTimeout(async () => {
      const sectionContent = button.closest('[data-results-section-content-for]');
      if (!sectionContent || button.classList.contains('is-loading')) return;

      const sectionId = sectionContent.dataset.resultsSectionContentFor;
      const initialRows = this.sectionInitialRows(sectionContent);
      const revealBatch = this.sectionRevealBatch(sectionContent);
      const sectionShownRowsCount = parseInt(sectionContent.dataset.sectionShown, 10) || initialRows;
      const endpoint = sectionContent.dataset.sectionResultsPath;
      const searchedText = sectionContent.dataset.searchedText;
      if (!sectionId || !endpoint || !searchedText) return;

      const tbody = sectionContent.querySelector('.search-results-table-new tbody');
      if (!tbody) return;

      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('section', sectionId);
      url.searchParams.set('section_shown_rows_count', String(sectionShownRowsCount));
      url.searchParams.set('limit', String(revealBatch));
      url.searchParams.set('searched_text', searchedText);
      this.appendFrozenDictionaryCollectionParams(url, sectionContent);
      this.appendBoxSearchParams(url, sectionContent);

      this.setSectionShowMoreLoading(button, true);
      button.removeAttribute('title');

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        });
        if (!response.ok) {
          console.error('Failed to load more section results:', response.status);
          button.title = sectionContent.dataset.loadErrorMessage || "Couldn't load more — try again";
          return;
        }

        const payload = await response.json();
        if (payload.rows_html) {
          tbody.insertAdjacentHTML('beforeend', payload.rows_html);
        }

        const shown = payload.shown ?? sectionShownRowsCount;
        sectionContent.dataset.sectionShown = String(shown);

        const showMoreRow = sectionContent.querySelector('.js-section-show-more-row');
        if (payload.has_more && showMoreRow && payload.button_label) {
          showMoreRow.style.display = '';
          this.setSectionShowMoreLabel(button, payload.button_label);
        } else if (showMoreRow) {
          showMoreRow.style.display = 'none';
        }

        this.initializeResultTooltips();
        this.initializeResultsGhostTooltips();
        if (typeof feather !== 'undefined') {
          feather.replace();
        }
        this.refreshWordBoxes();
        requestAnimationFrame(() => {
          this.syncResultsStickyHeaderColumnWidths();
          this.syncFixedNavWidth();
          this.updateResultsJumpLinks();
        });
      } catch (error) {
        console.error('Failed to load more section results:', error);
        button.title = sectionContent.dataset.loadErrorMessage || "Couldn't load more — try again";
      } finally {
        this.setSectionShowMoreLoading(button, false);
      }
    }, 100);
  }

  setSectionShowMoreLoading(button, isLoading) {
    if (!button) return;

    button.classList.toggle('is-loading', isLoading);
    button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  }

  setSectionShowMoreLabel(button, label) {
    if (!button) return;

    const labelEl = button.querySelector('.js-section-show-more-label');
    if (labelEl) {
      labelEl.textContent = label;
      return;
    }

    button.textContent = label;
  }

  // After collapse + reopen, keep only the first INITIAL rows and restore the capsule label.
  resetSectionShowMore(sectionContent) {
    const tbody = sectionContent.querySelector('.search-results-table-new tbody');
    if (!tbody) return;

    const initialRows = this.sectionInitialRows(sectionContent);
    const rows = Array.from(tbody.querySelectorAll('tr'));
    rows.forEach((row, index) => {
      if (index >= initialRows) row.remove();
    });

    const total = parseInt(sectionContent.dataset.sectionTotal, 10) || rows.length;
    sectionContent.dataset.sectionShown = String(Math.min(initialRows, total));

    const showMoreRow = sectionContent.querySelector('.js-section-show-more-row');
    const button = sectionContent.querySelector('.js-section-show-more-btn');
    if (!showMoreRow || !button) return;

    if (total <= initialRows) {
      showMoreRow.style.display = 'none';
      return;
    }

    showMoreRow.style.display = '';
    this.setSectionShowMoreLoading(button, false);
    const initialLabel = sectionContent.dataset.initialButtonLabel;
    if (initialLabel) this.setSectionShowMoreLabel(button, initialLabel);
  }

  sectionInitialRows(sectionContent) {
    return parseInt(sectionContent.dataset.sectionInitialRows, 10) || SECTION_INITIAL_VISIBLE_FALLBACK;
  }

  sectionRevealBatch(sectionContent) {
    return parseInt(sectionContent.dataset.sectionRevealBatch, 10) || SECTION_REVEAL_BATCH_FALLBACK;
  }

  // Replays dictionary filters frozen on the section at render time (not live checkbox state).
  appendFrozenDictionaryCollectionParams(url, sectionContent) {
    if (sectionContent.dataset.dictionaryFilterSubmitted !== 'true') return;

    url.searchParams.delete('dictionary_collection_ids[]');

    const ids = (sectionContent.dataset.dictionaryCollectionIds || '')
      .split(',')
      .map((id) => id.trim())
      .filter((id) => id.length > 0);

    if (ids.length > 0) {
      ids.forEach((id) => url.searchParams.append('dictionary_collection_ids[]', id));
    } else {
      url.searchParams.append('dictionary_collection_ids[]', '');
    }
  }

  // Word Decoder Show More must replay search_type=box (+ chain_pattern) so the server uses BoxSearchServiceOttoman.
  appendBoxSearchParams(url, sectionContent) {
    const pageParams = new URLSearchParams(window.location.search);
    const searchType = sectionContent.dataset.searchType || pageParams.get('search_type');
    if (searchType !== 'box') return;

    url.searchParams.set('search_type', 'box');

    const chainPattern = sectionContent.dataset.chainPattern || pageParams.get('chain_pattern');
    if (chainPattern) {
      url.searchParams.set('chain_pattern', chainPattern);
    }
  }

  scrollToResultsSection(event) {
    event.preventDefault();

    const jumpLink = event.currentTarget;
    const targetId = jumpLink.dataset.resultsJumpTarget;
    if (!targetId) return;

    const section = this.element.querySelector(`#results-section-${targetId}`);
    if (!section) return;

    // Offset = sticky column header height + fixed nav bar height (when visible)
    const headerBar      = this.element.querySelector('.results-column-header-sticky');
    const headerHeight   = headerBar ? headerBar.getBoundingClientRect().height : 0;
    const fixedNav       = this._cachedResultsFixedNav;
    const fixedNavHeight = (fixedNav && fixedNav.classList.contains('is-visible'))
      ? fixedNav.getBoundingClientRect().height
      : 0;

    // Extra breathing room so the section header isn't flush against the sticky bar.
    const scrollTop = section.getBoundingClientRect().top + window.pageYOffset - headerHeight - fixedNavHeight - SCROLL_BREATHING_ROOM;

    // Reflect clicked target immediately in the nav UI.
    this.pendingResultsJumpTargetId = targetId;
    if (this.pendingResultsJumpClearTimer) {
      clearTimeout(this.pendingResultsJumpClearTimer);
    }
    this.pendingResultsJumpClearTimer = setTimeout(() => {
      this.pendingResultsJumpTargetId = null;
      this.pendingResultsJumpClearTimer = null;
    }, 1200);
    this.setActiveResultsJumpLink(targetId);
    window.scrollTo({ top: scrollTop, behavior: 'smooth' });
  }

  updateResultsJumpLinks() {
    const jumpLinks = this._cachedJumpLinks || [];
    if (!jumpLinks.length) return;

    let activeTarget = jumpLinks[0].dataset.resultsJumpTarget;
    const threshold  = window.innerHeight * ACTIVE_SECTION_THRESHOLD_RATIO;
    let forcedActiveTarget = null;

    if (this.pendingResultsJumpTargetId) {
      const pendingSection = this.element.querySelector(`#results-section-${this.pendingResultsJumpTargetId}`);
      if (!pendingSection) {
        this.pendingResultsJumpTargetId = null;
      } else {
        const pendingTop = pendingSection.getBoundingClientRect().top;
        const pendingSettled = pendingTop <= threshold + 2;
        if (pendingSettled) {
          this.pendingResultsJumpTargetId = null;
          if (this.pendingResultsJumpClearTimer) {
            clearTimeout(this.pendingResultsJumpClearTimer);
            this.pendingResultsJumpClearTimer = null;
          }
        } else {
          forcedActiveTarget = this.pendingResultsJumpTargetId;
        }
      }
    }

    jumpLinks.forEach((jumpLink) => {
      const targetId  = jumpLink.dataset.resultsJumpTarget;
      const section   = this.element.querySelector(`#results-section-${targetId}`);
      if (!section) return;

      const sectionTop = section.getBoundingClientRect().top;
      if (sectionTop <= threshold) {
        activeTarget = targetId;
      }
    });

    if (forcedActiveTarget) {
      activeTarget = forcedActiveTarget;
    }

    jumpLinks.forEach((jumpLink) => {
      const isActive = jumpLink.dataset.resultsJumpTarget === activeTarget;
      jumpLink.classList.toggle('is-active', isActive);
    });

    // Show/hide the fixed sticky nav bar based on scroll position.
    const fixedNav = this._cachedResultsFixedNav;
    if (fixedNav) {
      const isVisible  = window.scrollY > FIXED_NAV_SHOW_SCROLL_PX;
      const wasVisible = fixedNav.classList.contains('is-visible');
      fixedNav.classList.toggle('is-visible', isVisible);
      fixedNav.setAttribute('aria-hidden', String(!isVisible));
      if (isVisible && !wasVisible) {
        this.syncFixedNavWidth();
      }
    }

    // Re-evaluate the column header pinned state after nav visibility may have changed.
    // Runs here (same rAF tick, after nav class update) so navBottom is accurate.
    if (this._stickyHeaderRefs) {
      const { sentinel, header, jumpToNav: nav, syncFixedPosition } = this._stickyHeaderRefs;
      this._tickStickyHeader(sentinel, header, nav, syncFixedPosition);
    }
  }

  setActiveResultsJumpLink(targetId) {
    if (!targetId) return;

    const jumpLinks = this._cachedJumpLinks || [];
    jumpLinks.forEach((jumpLink) => {
      const isActive = jumpLink.dataset.resultsJumpTarget === targetId;
      jumpLink.classList.toggle('is-active', isActive);
    });
  }

  initializeResultsGhostTooltips() {
    // A custom ghost tooltip is used here instead of Bootstrap's Tooltip because the
    // section headers are <button> elements whose click handler toggles collapse state.
    // Bootstrap tooltips on interactive elements interfere with the click event and
    // produce flicker when the DOM mutates on collapse. The ghost tooltip is a single
    // fixed-position div that is repositioned on mouseenter, avoiding those issues.
    this.resultsGhostTooltip = document.getElementById('resultsGhostTooltip');
    if (!this.resultsGhostTooltip) return;

    this.resultsGhostTooltipTriggers = Array.from(this.element.querySelectorAll('.js-results-has-tooltip'));
    if (!this.resultsGhostTooltipTriggers.length) return;

    this.resultsGhostTooltipHandlers = [];

    this.resultsGhostTooltipTriggers.forEach((trigger) => {
      const source = trigger.querySelector('.js-results-tooltip-source');
      if (!source) return;

      const isSectionHeader = trigger.classList.contains('results-section-header');
      const anchor = isSectionHeader
        ? (trigger.querySelector('.results-section-title') || trigger)
        : trigger;

      const onMouseEnter = () => {
        this.resultsGhostTooltip.style.width = 'min-content';
        this.resultsGhostTooltip.style.maxWidth = '300px';
        this.resultsGhostTooltip.innerHTML = source.innerHTML;
        this.resultsGhostTooltip.classList.add('active');
        this.updateResultsGhostTooltipPosition(anchor);
      };

      const onMouseLeave = () => {
        this.resultsGhostTooltip.classList.remove('active');
      };

      anchor.addEventListener('mouseenter', onMouseEnter);
      anchor.addEventListener('mouseleave', onMouseLeave);

      this.resultsGhostTooltipHandlers.push({ anchor, onMouseEnter, onMouseLeave });
    });
  }

  teardownResultsGhostTooltips() {
    if (!this.resultsGhostTooltipHandlers || !this.resultsGhostTooltipHandlers.length) return;

    this.resultsGhostTooltipHandlers.forEach(({ anchor, onMouseEnter, onMouseLeave }) => {
      anchor.removeEventListener('mouseenter', onMouseEnter);
      anchor.removeEventListener('mouseleave', onMouseLeave);
    });

    this.resultsGhostTooltipHandlers = [];
    if (this.resultsGhostTooltip) this.resultsGhostTooltip.classList.remove('active');
  }

  updateResultsGhostTooltipPosition(anchor) {
    if (!this.resultsGhostTooltip || !anchor) return;

    const anchorRect  = anchor.getBoundingClientRect();
    const tooltipRect = this.resultsGhostTooltip.getBoundingClientRect();

    let tooltipTop  = anchorRect.top - tooltipRect.height - GHOST_TOOLTIP_OFFSET;
    let tooltipLeft = anchorRect.left + (anchorRect.width / 2) - (tooltipRect.width / 2);

    if (tooltipTop < GHOST_TOOLTIP_VIEWPORT_PADDING) {
      tooltipTop = anchorRect.bottom + GHOST_TOOLTIP_VIEWPORT_PADDING;
    }

    const tooltipBottom = tooltipTop + tooltipRect.height;
    if (tooltipBottom > window.innerHeight - GHOST_TOOLTIP_VIEWPORT_PADDING) {
      tooltipTop = Math.max(GHOST_TOOLTIP_VIEWPORT_PADDING, window.innerHeight - tooltipRect.height - GHOST_TOOLTIP_VIEWPORT_PADDING);
    }

    if (tooltipLeft < GHOST_TOOLTIP_VIEWPORT_PADDING) {
      tooltipLeft = GHOST_TOOLTIP_VIEWPORT_PADDING;
    }

    if (tooltipLeft + tooltipRect.width > window.innerWidth - GHOST_TOOLTIP_VIEWPORT_PADDING) {
      tooltipLeft = window.innerWidth - tooltipRect.width - GHOST_TOOLTIP_VIEWPORT_PADDING;
    }

    this.resultsGhostTooltip.style.top  = `${tooltipTop}px`;
    this.resultsGhostTooltip.style.left = `${tooltipLeft}px`;
  }

  // Builds the LexiQamus URL that the ENG definition iframe will load (search_type=definition, iframe=true).
  buildEngDefinitionSearchEmbedUrl(searchedText) {
    const baseUrl = this.definitionSearchBaseUrlValue
    const url = new URL(baseUrl, window.location.origin)
    url.searchParams.set("search_type", "definition")
    url.searchParams.set("_word", searchedText)
    url.searchParams.set("iframe", "true")
    return url.toString()
  }

  // Tell the browser which page to load inside the ENG definition iframe.
  // Our JS does not read .src again — the browser fetches the URL and renders results inside the iframe.
  // The loaded page may postMessage its height back (see handleDefinitionIframeResize).
  setDefinitionIframeSrc(src) {
    if (!this.hasDefinitionIframeTarget) return
    // src e.g. "https://lab.lexiqamus.com/en/buildlq?search_type=definition&_word=hello&iframe=true" or BLANK_IFRAME_SRC
    if (src === BLANK_IFRAME_SRC) {
      this.definitionIframeTarget.src = BLANK_IFRAME_SRC
      return
    }

    this.definitionIframeTarget.addEventListener("load", this.boundHandleDefinitionIframeLoad, { once: true })
    this.definitionIframeTarget.src = src
  }

  // Shows the loading spinner and marks the indicator as busy for assistive technologies.
  showDefinitionSearchLoading() {
    if (!this.hasDefinitionSearchLoadingIndicatorTarget) return
    this.definitionSearchLoadingIndicatorTarget.hidden = false
    this.definitionSearchLoadingIndicatorTarget.setAttribute("aria-busy", "true")
  }

  // Cancels the fallback timer (if running) and hides the loading spinner.
  hideDefinitionSearchLoading() {
    if (this.definitionSearchLoadingFallbackTimer) {
      clearTimeout(this.definitionSearchLoadingFallbackTimer)
      this.definitionSearchLoadingFallbackTimer = null
    }
    if (!this.hasDefinitionSearchLoadingIndicatorTarget) return
    this.definitionSearchLoadingIndicatorTarget.hidden = true
    this.definitionSearchLoadingIndicatorTarget.setAttribute("aria-busy", "false")
  }

  // Fired once the iframe finishes loading. Starts a 15s fallback timer to hide the spinner
  // in case the embed never posts its resize message (e.g. an error page is shown instead).
  handleDefinitionIframeLoad() {
    this.definitionSearchLoadingFallbackTimer = window.setTimeout(() => {
      this.hideDefinitionSearchLoading()
      if (this.hasDefinitionIframeContainerTarget) {
        this.definitionIframeContainerTarget.hidden = false
        this.syncLexiqamusSearchBarActionButtons()
      }
    }, 15000)
  }

  // ENG only: loads definition search in the iframe and reveals the results container below the search bar.
  loadEngDefinitionSearchInIframe(searchedText) {
    if (!this.hasDefinitionIframeTarget) return

    window.hideLexiqamusInnovationsLanding()
    this.showDefinitionSearchLoading()
    if (this.hasDefinitionIframeContainerTarget) {
      this.definitionIframeContainerTarget.hidden = true
    }
    this.setDefinitionIframeSrc(this.buildEngDefinitionSearchEmbedUrl(searchedText))
    this.resetDefinitionIframeHeight()
  }

  // Clears iframe and hides the results container; restores the innovations landing.
  hideEngDefinitionIframe() {
    this.hideDefinitionSearchLoading()
    if (this.hasDefinitionIframeContainerTarget) {
      this.definitionIframeContainerTarget.hidden = true
    }
    this.setDefinitionIframeSrc(BLANK_IFRAME_SRC)
    this.resetDefinitionIframeHeight()
    this.syncLexiqamusSearchBarActionButtons()
  }

  // Resets iframe to its initial height before a new search loads.
  resetDefinitionIframeHeight() {
    if (!this.hasDefinitionIframeTarget) return
    this.definitionIframeTarget.style.height = `${this.definitionIframeDefaultHeight}px`
  }

  // Receives postMessage from the LexiQamus embed page and resizes the iframe to fit its content.
  handleDefinitionIframeResize(event) {
    if (!this.hasDefinitionIframeTarget) return
    if (event.source !== this.definitionIframeTarget.contentWindow) return
    if (event.data?.type !== BUILDLQ_EMBED_RESIZE_MESSAGE_TYPE) return

    const height = Number.parseInt(event.data.height, 10)
    if (!Number.isFinite(height) || height <= 0) return

    this.hideDefinitionSearchLoading()
    if (this.hasDefinitionIframeContainerTarget) {
      this.definitionIframeContainerTarget.hidden = false
    }
    this.definitionIframeTarget.style.height = `${height}px`
    this.syncLexiqamusSearchBarActionButtons()
  }

  syncLexiqamusSearchBarActionButtons() {
    const barEl = this.element.querySelector(".js-lexiqamus-search-bar")
      || document.querySelector(".js-lexiqamus-search-bar")
    if (!barEl) return

    const ctrl = this.application.getControllerForElementAndIdentifier(barEl, "lexiqamus-search-bar")
    ctrl?.updateActionButtonsVisibility()
  }

  // Handle nested tab switching (mini-tabs within each pane)
  switchTab(event) {
    event.preventDefault();

    const clickedButton = event.currentTarget;
    const searchBySpellingContainer = clickedButton.closest('.js-search-by-spelling-container') || document;
    const searchBySpellingPane = searchBySpellingContainer.closest('.tab-pane');

    // Get the corresponding table ID
    const targetTableSelector = clickedButton.getAttribute('href');
    
    // Check if the target table exists before proceeding
    const targetTable = searchBySpellingContainer.querySelector(targetTableSelector);
    if (!targetTable) {
      console.warn(`Table not found for ID: ${targetTableSelector}`);
      return;
    }

    // Remove active class from all buttons within the same pane
    const allTabButtons = searchBySpellingContainer.querySelectorAll('.js-search-results-tab-btn');
    allTabButtons.forEach(button => {
      button.classList.remove('active', 'btn-outline-primary');
      button.classList.add('btn-outline-secondary');
    });

    // Add active class to clicked button
    clickedButton.classList.remove('btn-outline-secondary');
    clickedButton.classList.add('active', 'btn-outline-primary');

    // Hide all tables within the same pane
    searchBySpellingContainer.querySelectorAll('.js-search-results-table').forEach(table => {
      table.classList.add('d-none');
    });

    // Show the selected table
    targetTable.classList.remove('d-none');

    // Show or hide main-table pagination: only when main table (id ending with -main) is visible
    if (searchBySpellingPane) {
      const mainPagination = searchBySpellingPane.querySelector('.js-main-table-pagination');
      if (mainPagination) {
        const isMainTable = (targetTableSelector && String(targetTableSelector).indexOf('-main') !== -1);
        mainPagination.classList.toggle('d-none', !isMainTable);
      }
    }
    // Sync footer tables to avoid duplicate display
    this.syncFooterTablesVisibility(targetTableSelector, targetTable);
    this.updateCountsForSelectedSearchedBySpelling(targetTable);
    this.refreshWordBoxes();
    this.syncDictionaryColumnAlignmentOffset();
    this.syncResultsStickyHeaderColumnWidths();
    this.syncFixedNavWidth();
  }

  // Surface header "Search by Spelling" should behave like distinct value tabs:
  // operate on the currently active main tab pane (exact/partial/sounds-like).
  switchTabFromHeader(event) {
    event.preventDefault();

    const clickedButton = event.currentTarget;
    const searchBySpellingPane = document.querySelector('.js-search-by-spelling-pane.show.active') || document.querySelector('.js-search-by-spelling-pane.active');
    const searchBySpellingContainer = (searchBySpellingPane && searchBySpellingPane.querySelector('.js-search-by-spelling-container')) || document;

    const isMainDistinctValue = clickedButton.dataset.distinctValueMain === 'true';
    let targetTable = null;

    if (isMainDistinctValue) {
      targetTable = searchBySpellingContainer.querySelector('.js-search-results-table[id$="-main"]');
    } else {
      const distinctValue = clickedButton.dataset.distinctValue || '';
      if (distinctValue) {
        targetTable = Array.from(searchBySpellingContainer.querySelectorAll('.js-search-results-table')).find((t) => t.dataset.distinctValue === distinctValue);
      }
    }

    if (!targetTable) {
      console.warn('Table not found for Surface Header spelling button');
      return;
    }

    // Update active state within the Surface Header group only
    const spellingButtonsRow = clickedButton.closest('.spelling-btns-row') || document;
    spellingButtonsRow.querySelectorAll('.js-surface-header-spelling-btn').forEach((spellingButton) => {
      spellingButton.classList.remove('active');
    });
    clickedButton.classList.add('active');

    // Update Surface Header title (distinct value text) and theme to the selected variant
    const distinctValue = clickedButton.dataset.distinctValue || '';
    const surfaceHeaderTitleElement = document.querySelector('.js-surface-header-title');
    const isOttoman = clickedButton.classList.contains('osm');
    if (surfaceHeaderTitleElement && distinctValue) {
      surfaceHeaderTitleElement.textContent = distinctValue;
      surfaceHeaderTitleElement.classList.remove('search-result-ottoman-text', 'search-result-latin-text');
      surfaceHeaderTitleElement.classList.add(isOttoman ? 'search-result-ottoman-text' : 'search-result-latin-text');
    }
    // Update main card accent color (burgundy vs navy) to match selected variant
    const mainSearchResultsCard = this.mainSearchResultsCard();
    if (mainSearchResultsCard) {
      mainSearchResultsCard.classList.remove('main-search-results-card-theme-ottoman', 'main-search-results-card-theme-latin');
      mainSearchResultsCard.classList.add(isOttoman ? 'main-search-results-card-theme-ottoman' : 'main-search-results-card-theme-latin');
    }

    // Hide all tables within the same pane
    searchBySpellingContainer.querySelectorAll('.js-search-results-table').forEach((table) => {
      table.classList.add('d-none');
    });

    // Show the selected table
    targetTable.classList.remove('d-none');

    // Show/hide main-table pagination same as switchTab()
    if (searchBySpellingPane) {
      const mainPagination = searchBySpellingPane.querySelector('.js-main-table-pagination');
      if (mainPagination) {
        const isMainTable = String(targetTable.id).indexOf('-main') !== -1;
        mainPagination.classList.toggle('d-none', !isMainTable);
      }
    }

    this.syncFooterTablesVisibility(`#${targetTable.id}`, targetTable);
    this.refreshWordBoxes();
    this.syncDictionaryColumnAlignmentOffset();
    this.syncResultsStickyHeaderColumnWidths();
    this.syncFixedNavWidth();
    this.initializeResultTooltips();
    this.updateCountsForSelectedSearchedBySpelling(targetTable);
    
  }

  updateSurfaceHeaderRecordCountFromTable(tableContainer) {
    const count = this.countRowsForTable(tableContainer);
    this.updateSurfaceHeaderRecordCount(count);
  }

  countRowsForTable(tableContainer) {
    if (!tableContainer) return 0;
    // Count only body rows so header rows are excluded.
    const tbody = tableContainer.querySelector('table tbody');
    if (!tbody) return 0;

    return tbody.querySelectorAll('tr').length;
  }

  updateSurfaceHeaderRecordCount(count) {
    const countElement = document.querySelector('.js-surface-header-record-count');
    if (countElement) {
      countElement.textContent = String(count);
    }

    const labelElement = document.querySelector('.js-surface-header-record-label');
    if (!labelElement) return;

    // Use localized singular/plural strings passed by the server-side view.
    const singular = this.recordsFoundSingularValue || 'record found';
    const plural = this.recordsFoundPluralValue || 'records found';
    labelElement.textContent = count === 1 ? singular : plural;
  }

  // Sets header count to the precomputed overall total (exact + partial + sounds-like),
  // with an optional fallback to the currently visible table if total is unavailable.
  updateSurfaceHeaderRecordCountFromOverall(fallbackTableContainer = null) {
    if (this.hasOverallRecordsCountValue) {
      this.updateSurfaceHeaderRecordCount(this.overallRecordsCountValue);
      return;
    }
    if (fallbackTableContainer) {
      this.updateSurfaceHeaderRecordCountFromTable(fallbackTableContainer);
    }
  }

  // Single entry point after a table switch:
  // keep the left header total tied to the original searched-word result count.
  updateCountsForSelectedSearchedBySpelling(referenceTable) {
    this.updateSurfaceHeaderRecordCountFromOverall(referenceTable);
  }

  // Prevents duplicate tables: when a tab value table is shown in the main area,
  // its corresponding pre-populated footer table is hidden, and vice versa.
  // Supports both: footer inside js-tab-results-footer-placeholder (lazy-loaded) and
  // preloaded footer (preloaded table + js-tab-results-footer-remaining).
  syncFooterTablesVisibility(tableId, shownTable) {
    const footerPlaceholder = document.getElementById('js-tab-results-footer-placeholder');
    const footerRemaining = document.getElementById('js-tab-results-footer-remaining');
    const footerContainer = footerPlaceholder || (footerRemaining && footerRemaining.parentElement);
    if (!footerContainer) return;

    const isMainTable = tableId.endsWith('-main');

    // Show all footer tables first (reset)
    footerContainer.querySelectorAll('.js-search-results-table').forEach(table => {
      table.classList.remove('d-none');
    });

    // If a tab value table is being shown (not the main table),
    // hide the matching footer table to avoid duplicate display.
    if (!isMainTable) {
      const distinctValue = shownTable.dataset.distinctValue;
      if (distinctValue) {
        footerContainer.querySelectorAll('.js-search-results-table').forEach(table => {
          if (table.dataset.distinctValue === distinctValue) {
            table.classList.add('d-none');
          }
        });
      }
    }
  }

  setupMainTabActivation() {
    // TODO: Prefer declarative Stimulus actions in the view for `shown.bs.tab`
    // (e.g., `shown.bs.tab->search-output#onMainTabShown`) instead of binding listeners here.
    // Bootstrap tab "shown" event to reveal the pane's main table and log tab clicks
    document.querySelectorAll('#searchResultsTabs button[data-bs-toggle="tab"]').forEach(mainTabButton => {
      mainTabButton.addEventListener('shown.bs.tab', (e) => {
        this.onMainTabShown(e);
        this.logSearchesOnLqSearchResults(e); // Log Tab Search
      });
    });
  }

  /**
   * Activate the value tab from URL params (active_value_index). Called when tab results
   * have been injected into the DOM (searchOutputTabResultsLoaded) so the value tab button exists.
   * Only activates when coming from footer pagination (active_value_page > 1).
   */
  activateSearchTabFromParams() {
    const params = new URLSearchParams(window.location.search);
    const activeValueIndex = params.get('active_value_index');
    const activeValuePage = params.get('active_value_page');
    const pageNum = parseInt(activeValuePage, 10);
    const paginationClicked = activeValuePage !== null && activeValuePage !== '' && !isNaN(pageNum) && pageNum > 1;

    if (activeValueIndex === null || activeValueIndex === '' || !paginationClicked) return;

    const index = parseInt(activeValueIndex, 10);
    if (isNaN(index) || index < 0) return;

    const mainTab = params.get('tab') || 'exact_match';
    this.activateTabValueByIndex(index, mainTab);
  }

  /**
   * Activate the value tab (search term / Ottoman value) at the given index within the current main tab pane.
   * Tab table and footer table share the same page param, so both show the same page (e.g. page 2) without scrolling.
   * @param {number} index - 0 = main table (search term), 1+ = footer value tables
   * @param {string} mainTab - 'exact_match' | 'sounds_like'
   */
  activateTabValueByIndex(index, mainTab) {
    const mainTabId = (mainTab || 'exact_match').replace(/_/g, '-');
    const searchBySpellingPane = document.querySelector(`#${mainTabId}-tab`);
    if (!searchBySpellingPane) return;

    const searchBySpellingContainer = searchBySpellingPane.querySelector('.js-search-by-spelling-container');
    if (!searchBySpellingContainer) return;

    // Value tab buttons have href like "#table-xxx-ottoman-0" (Latin) or "#table-xxx-latin-0" (Ottoman). Match by index at end.
    const valueTabButtonSelector = `a[href$="-${index}"], button.js-search-results-tab-btn[href$="-${index}"]`;
    const valueTabButton = searchBySpellingContainer.querySelector(valueTabButtonSelector);
    if (valueTabButton) {
      valueTabButton.click();
    }
  }

  onMainTabShown(e) {
    const mainTabPaneSelector = e.target.getAttribute('data-bs-target'); // e.g., "#exact-match-tab" or "#sounds-like-tab"
    const searchBySpellingPane = document.querySelector(mainTabPaneSelector);
    if (!searchBySpellingPane) return;

    const searchBySpellingContainer = searchBySpellingPane.querySelector('.js-search-by-spelling-container');
    if (!searchBySpellingContainer) return;

    // Show footer tables row only for exact match tab; hide for sounds like
    const footerRow = document.getElementById('js-search-footer-tables-row');
    if (footerRow) {
      footerRow.classList.toggle('d-none', mainTabPaneSelector !== '#exact-match-tab');
    }

    // Hide all tables in this pane
    searchBySpellingContainer.querySelectorAll('.js-search-results-table').forEach(table => {
      table.classList.add('d-none');
    });

    // Show the pane's main table (id ends with "-main")
    const mainTable = searchBySpellingContainer.querySelector('.js-search-results-table[id$="-main"]');
    if (mainTable) {
      mainTable.classList.remove('d-none');
      this.updateSurfaceHeaderRecordCountFromOverall(mainTable);
    }

    // Reset mini-tab buttons state within this pane
    const miniTabButtons = searchBySpellingContainer.querySelectorAll('.js-search-results-tab-btn');
    miniTabButtons.forEach(miniTabButton => {
      miniTabButton.classList.remove('active', 'btn-outline-primary');
      miniTabButton.classList.add('btn-outline-secondary');
    });

    // Activate the mini-tab button that points to the main table
    if (mainTable) {
      const mainMiniTabButton = Array.from(miniTabButtons).find(candidateMiniTabButton => candidateMiniTabButton.getAttribute('href') === `#${mainTable.id}`);
      if (mainMiniTabButton) {
        mainMiniTabButton.classList.remove('btn-outline-secondary');
        mainMiniTabButton.classList.add('active', 'btn-outline-primary');
      }
    }

    // Keep surface-header "Search by Spelling" in sync with main tab clicks:
    // selecting a main tab should also select the main spelling button.
    const mainSearchedBySpellingButton = this.element.querySelector('.js-surface-header-spelling-btn[data-distinct-value-main="true"]');
    if (mainSearchedBySpellingButton && !mainSearchedBySpellingButton.classList.contains('active')) {
      this.switchTabFromHeader({
        preventDefault: () => {},
        currentTarget: mainSearchedBySpellingButton
      });
    }

    // When switching main tabs, the main table is shown, so all footer tables should be visible
    const footerPlaceholder = document.getElementById('js-tab-results-footer-placeholder');
    const footerRemaining = document.getElementById('js-tab-results-footer-remaining');
    const footerContainer = footerPlaceholder || (footerRemaining && footerRemaining.parentElement);
    if (footerContainer) {
      footerContainer.querySelectorAll('.js-search-results-table').forEach(table => {
        table.classList.remove('d-none');
      });
    }

    this.refreshWordBoxes();
    this.syncDictionaryColumnAlignmentOffset();
    this.initializeResultTooltips();
  }

  // Re-measures word chips after initial load and AJAX tab injection (searchOutputTabResultsLoaded).
  // Only tags multiline chips — never forces first-line width. Former fitWordBoxToFirstLine() shrank
  // long Phrases & Compounds ~1s after tab load (with overflow:hidden truncating the chip).
  refreshWordBoxes() {
    // Wait until tab visibility/layout settles before measuring line wrapping.
    requestAnimationFrame(() => {
      const boxes = document.querySelectorAll(
        '.search-results-table-new .result-box-latin, ' +
        '.search-results-table-new .result-box-ottoman, ' +
        '.search-results-table-new .entry-box'
      );

      boxes.forEach((box) => {
        box.classList.remove('js-word-box-multiline');
        // Clear prior inline width before recalculating for current viewport/content.
        box.style.width = '';

        const lineHeight = this.wordBoxLineHeight(box);
        if (!lineHeight) return;

        const isMultiline = this.wordBoxIsMultiline(box, lineHeight);
        if (isMultiline) {
          // Allow wrapped chips to grow vertically; do not shrink to first-line width
          // (that + overflow:hidden was truncating long Phrases/Compounds after tab load).
          box.classList.add('js-word-box-multiline');
        }
      });
      this.syncResultsStickyHeaderColumnWidths();
      this.syncFixedNavWidth();
    });
  }

  // Extracts the computed line-height of a word box element
  // Returns null if line-height cannot be determined
  wordBoxLineHeight(box) {
    const styles = window.getComputedStyle(box);
    const lineHeight = parseFloat(styles.lineHeight);
    if (!lineHeight || Number.isNaN(lineHeight)) return null;
    return lineHeight;
  }

  // Determines if a word box spans multiple lines by comparing content height to line height.
  // Excludes vertical padding so padded single-line Latin boxes are not misclassified.
  // Temporarily removes the copy button from layout during measurement.
  wordBoxIsMultiline(box, lineHeight) {
    const copyButton = box.querySelector('.search-result-word-box-copy');
    let previousDisplay = null;
    if (copyButton && copyButton.offsetParent !== null) {
      previousDisplay = copyButton.style.display;
      copyButton.style.display = 'none';
    }

    const styles = window.getComputedStyle(box);
    const paddingTop = parseFloat(styles.paddingTop) || 0;
    const paddingBottom = parseFloat(styles.paddingBottom) || 0;
    const contentHeight = box.scrollHeight - paddingTop - paddingBottom;
    const isMultiline = contentHeight > (lineHeight * WORD_BOX_MULTILINE_HEIGHT_RATIO);

    if (copyButton && previousDisplay !== null) {
      copyButton.style.display = previousDisplay;
    }
    return isMultiline;
  }

  // Sizes and positions the fixed sticky nav outer element to exactly match the
  // main search results card width and horizontal position in the viewport.
  // The fixed nav is position:fixed — JS sets left+width directly from the card's
  // getBoundingClientRect so no CSS centering tricks are needed.
  syncFixedNavWidth() {
    const fixedNav = this.element.querySelector('.js-results-fixed-sticky-nav');
    if (!fixedNav) return;

    const mainCard = this.mainSearchResultsCard();
    if (!mainCard) return;

    const cardRect = mainCard.getBoundingClientRect();
    if (!cardRect.width || Number.isNaN(cardRect.width)) return;

    // Set left and width directly — CSS transform is now translateY only (no translateX).
    fixedNav.style.setProperty("--results-fixed-nav-left", `${Math.round(cardRect.left)}px`);
    fixedNav.style.setProperty("--results-fixed-nav-width", `${Math.round(cardRect.width)}px`);
  }

  mainSearchResultsCard() {
    return this.element.querySelector(SEARCH_RESULTS_CARD_SELECTOR)
      || document.querySelector(SEARCH_RESULTS_CARD_SELECTOR);
  }

  syncResultsStickyHeaderColumnWidths() {
    // Re-sync fixed position whenever column widths are recalculated.
    if (this._stickyHeaderRefs && this._stickyHeaderRefs.syncFixedPosition) {
      this._stickyHeaderRefs.syncFixedPosition();
    }

    const stickyHeaderTable = this.element.querySelector('.results-column-header-sticky .search-results-table-new');
    if (!stickyHeaderTable) return;

    const stickyHeaderCells = stickyHeaderTable.querySelectorAll('thead th');
    if (!stickyHeaderCells.length) return;

    // Use cached layout elements; filter to visible ones each time (visibility can change).
    const allLayouts     = this._cachedResultsLayouts || Array.from(this.element.querySelectorAll('.js-results-list-layout'));
    const visibleLayouts = allLayouts.filter((layout) => layout.offsetParent !== null);
    if (!visibleLayouts.length) return;

    visibleLayouts.forEach((layout) => {
      // Prefer Exact Matches row first for stable alignment, then fallback.
      const sampleRow = layout.querySelector(
        '#results-section-exact .results-section-content:not(.is-collapsed) .search-results-table-new tbody tr'
      ) || layout.querySelector(
        '.results-section-content:not(.is-collapsed) .search-results-table-new tbody tr'
      ) || layout.querySelector('.results-section-content .search-results-table-new tbody tr');
      if (!sampleRow) return;

      const sampleCells = sampleRow.querySelectorAll('td');
      const bodyColumnWidths = Array.from(sampleCells).map((cell) => cell.getBoundingClientRect().width);
      const measuredWidths = this._stickyHeaderColumnWidthsFromBody(
        stickyHeaderTable,
        stickyHeaderCells.length,
        bodyColumnWidths
      );
      if (!measuredWidths) return;

      // Never hard-lock sticky table width; it can clip columns in constrained layouts.
      // We only sync per-column widths and keep their total pixel-perfect.
      stickyHeaderTable.style.width = '';
      stickyHeaderTable.style.minWidth = '';
      stickyHeaderTable.style.maxWidth = '';
      const totalMeasuredWidth = measuredWidths.reduce((sum, width) => sum + width, 0);
      if (!totalMeasuredWidth || Number.isNaN(totalMeasuredWidth)) return;

      let assignedWidth = 0;
      measuredWidths.forEach((cellWidth, index) => {
        if (!cellWidth || Number.isNaN(cellWidth)) return;

        const stickyCell = stickyHeaderCells[index];
        const isLastColumn = index === measuredWidths.length - 1;
        const normalizedWidth = isLastColumn
          ? Math.max(totalMeasuredWidth - assignedWidth, 0)
          : cellWidth;
        assignedWidth += normalizedWidth;

        const normalizedWidthPx = `${normalizedWidth.toFixed(4)}px`;
        stickyCell.style.width = normalizedWidthPx;
        stickyCell.style.minWidth = normalizedWidthPx;
        stickyCell.style.maxWidth = normalizedWidthPx;
      });
    });
  }

  // Sticky header rows use colspan on headword th; body rows use one td per column.
  // Map measured body column widths onto sticky th cells so category (and neighbors) stay aligned.
  _stickyHeaderColumnWidthsFromBody(stickyHeaderTable, stickyColumnCount, bodyColumnWidths) {
    const bodyColumnCount = bodyColumnWidths.length;
    if (!bodyColumnCount) return null;

    if (bodyColumnCount === stickyColumnCount) {
      return bodyColumnWidths;
    }

    const isAiFeedbackLayout = stickyHeaderTable.classList.contains('search-results-table-new--ai-feedback-column');

    if (isAiFeedbackLayout && bodyColumnCount === 7 && stickyColumnCount === 5) {
      return [
        bodyColumnWidths[0],
        bodyColumnWidths[1],
        bodyColumnWidths[2],
        bodyColumnWidths[3] + bodyColumnWidths[4] + bodyColumnWidths[5],
        bodyColumnWidths[6]
      ];
    }

    if (!isAiFeedbackLayout && bodyColumnCount === 5 && stickyColumnCount === 4) {
      return [
        bodyColumnWidths[0],
        bodyColumnWidths[1],
        bodyColumnWidths[2] + bodyColumnWidths[3],
        bodyColumnWidths[4]
      ];
    }

    return null;
  }

  handleTabResultsLoaded(event) {
    // Re-initialize dynamic table UI after AJAX tab/footer injection.
    this.refreshWordBoxes();
    this.syncDictionaryColumnAlignmentOffset();
    this.syncResultsStickyHeaderColumnWidths();
    this.syncFixedNavWidth();
    this.initializeResultTooltips();
    const incomingTotals = event && event.detail && event.detail.spellingTotals;
    if (incomingTotals && typeof incomingTotals === 'object') {
      const existingTotals = this.hasSpellingTotalsValue ? (this.spellingTotalsValue || {}) : {};
      this.spellingTotalsValue = { ...existingTotals, ...incomingTotals };
    }

    const activeSurfaceBtn = document.querySelector('.js-surface-header-spelling-btn.active');
    if (activeSurfaceBtn) {
      // Keep left header count fixed to the original searched-word totals
      // even when the active spelling variant changes.
      this.updateSurfaceHeaderRecordCountFromOverall();
    }
  }

  syncDictionaryColumnAlignmentOffset() {
    if (!this.hasDictionaryColumnHeaderLabelTarget || !this.hasResultsTableTarget) return;

    this.dictionaryColumnHeaderLabelTargets.forEach((headerLabel) => {
      const parentTable = headerLabel.closest('[data-search-output-target="resultsTable"]');
      if (!parentTable) return;

      const labelWidth = headerLabel.getBoundingClientRect().width;
      if (!labelWidth || Number.isNaN(labelWidth)) return;

      // Keep this CSS variable name in sync with search_page.scss.
      // If `--dictionary-header-half-width` is renamed in CSS,
      // update this setter and the consumer rule together.
      parentTable.style.setProperty('--dictionary-header-half-width', `${labelWidth / 2}px`);
    });
  }

  initializeResultTooltips() {
    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) return;

    document
      .querySelectorAll(
        '.search-results-surface [data-bs-toggle="tooltip"], .search-results-surface .js-search-output-cite-btn[title]'
      )
      .forEach((el) => {
        const instance = bootstrap.Tooltip.getInstance(el);
        if (instance) instance.dispose();

        const isManualTooltip = el.matches(this.wordBoxTooltipSelector) || el.matches(this.wordBoxCopyTooltipSelector);
        new bootstrap.Tooltip(el, isManualTooltip ? { trigger: 'manual' } : {});
      });

    this.attachNestedTooltipListeners();

    if (typeof feather !== 'undefined') {
      feather.replace();
    }
  }

  // GROUP-CELL TOOLTIP SELECTOR
  // Matches the two wide <td> cells (Result + Entry) that carry a parent-level tooltip.
  // Kept as a constant so all methods stay in sync when class names change.
  get groupCellTooltipSelector() {
    return '[data-search-output-target="resultsTable"] .js-group-cell-tooltip';
  }

  // WORD-BOX TOOLTIP SELECTOR
  // Matches clickable result/entry word chips. Nested child with its own tooltip is
  // only the copy button — TypeO dot is a sibling in search-result-misspelled-anchor.
  get wordBoxTooltipSelector() {
    return '[data-search-output-target="resultsTable"] .js-result-box[data-bs-toggle="tooltip"], [data-search-output-target="resultsTable"] .js-entry-box[data-bs-toggle="tooltip"]';
  }

  // Combined parent selectors handled by one document mouseover/mouseout pair.
  get nestedTooltipParentSelector() {
    return `${this.groupCellTooltipSelector}, ${this.wordBoxTooltipSelector}`;
  }

  // Copy icon nested inside word chips — manual trigger so show/hide stays in sync with parent.
  get wordBoxCopyTooltipSelector() {
    return '[data-search-output-target="resultsTable"] .js-search-result-word-box-copy[data-bs-toggle="tooltip"]';
  }

  // Attaches one delegated mouseover/mouseout pair for all nested parent tooltips.
  attachNestedTooltipListeners() {
    if (this.nestedTooltipListenersAttached) return;

    document.addEventListener('mouseover', this.boundNestedTooltipMouseOver);
    document.addEventListener('mouseout', this.boundNestedTooltipMouseOut);
    this.nestedTooltipListenersAttached = true;
  }

  // Removes the delegated listeners and disposes nested parent + copy-button tooltip instances.
  teardownNestedTooltipListeners() {
    if (!this.nestedTooltipListenersAttached) return;

    document.removeEventListener('mouseover', this.boundNestedTooltipMouseOver);
    document.removeEventListener('mouseout', this.boundNestedTooltipMouseOut);
    this.nestedTooltipListenersAttached = false;

    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) return;
    [this.nestedTooltipParentSelector, this.wordBoxCopyTooltipSelector].forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        const tooltipInstance = bootstrap.Tooltip.getInstance(el);
        if (tooltipInstance) {
          tooltipInstance.hide();
          tooltipInstance.dispose();
        }
      });
    });
  }

  // Shows a parent tooltip when the cursor is over its bare area; hides it when the
  // cursor moves onto a nested child that has its own tooltip (Bootstrap has no
  // built-in parent/child suppression — see PR #1825).
  handleNestedTooltipMouseOver(event, parentSelector) {
    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) return;

    const wordBoxEl = event.target.closest(this.wordBoxTooltipSelector);
    if (wordBoxEl) {
      // Word box / copy icon sit inside the group-cell <td>. closest() only resolves
      // the inner parent, so suppress outer group-cell tooltips explicitly.
      this.hideOuterGroupCellTooltips(wordBoxEl);
    }

    const parentEl = event.target.closest(parentSelector);
    if (!parentEl) return;

    const parentTooltip = bootstrap.Tooltip.getInstance(parentEl);
    if (!parentTooltip) return;

    const childTooltipEl = this.nestedChildTooltipElement(event.target, parentEl);
    const isHoveringChildTooltip = childTooltipEl && childTooltipEl !== parentEl;

    if (isHoveringChildTooltip) {
      parentTooltip.hide();
      bootstrap.Tooltip.getInstance(childTooltipEl)?.show();
    } else {
      parentEl.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        if (el !== parentEl) bootstrap.Tooltip.getInstance(el)?.hide();
      });
      parentTooltip.show();
    }
  }

  // Finds the nested child tooltip element within a parent tooltip container.
  // Word-box parents only nest the copy button; group cells may nest badges, etc.
  // Returns null if target is the parent itself or no parent exists.
  nestedChildTooltipElement(target, parentEl) {
    if (!parentEl || target === parentEl) return null;

    if (parentEl.matches(this.wordBoxTooltipSelector)) {
      return target.closest(this.wordBoxCopyTooltipSelector);
    }

    // Latin hover-feedback card (Correct / Suggest edit) has no [data-bs-toggle="tooltip"]
    // of its own, so the generic lookup below would miss it and let the group-cell
    // tooltip ("Go to this result" / "Go to the headword") show through underneath.
    const feedbackWrap = target.closest('.search-result-latin-feedback-wrap');
    if (feedbackWrap && parentEl.contains(feedbackWrap)) return feedbackWrap;

    return target.closest('[data-bs-toggle="tooltip"]');
  }

  // Walks up the DOM tree from the given element and hides any group-cell tooltips
  // found in the ancestor chain. Used to prevent parent tooltips from staying visible
  // when a nested child tooltip is shown.
  hideOuterGroupCellTooltips(fromElement) {
    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) return;

    let el = fromElement.parentElement;
    while (el && el !== document) {
      if (el.matches(this.groupCellTooltipSelector)) {
        bootstrap.Tooltip.getInstance(el)?.hide();
      }
      el = el.parentElement;
    }
  }

  // Hides the parent tooltip when the cursor leaves it entirely; restores it when the
  // cursor leaves a nested child tooltip and returns to the parent area.
  handleNestedTooltipMouseOut(event, parentSelector) {
    if (typeof bootstrap === 'undefined' || !bootstrap.Tooltip) return;

    const parentEl = event.target.closest(parentSelector);
    if (!parentEl) return;

    const parentTooltip = bootstrap.Tooltip.getInstance(parentEl);
    if (!parentTooltip) return;

    const leftChildTooltipEl = this.nestedChildTooltipElement(event.target, parentEl);
    if (leftChildTooltipEl && leftChildTooltipEl !== parentEl) {
      const leavingChild =
        !event.relatedTarget || !leftChildTooltipEl.contains(event.relatedTarget);
      if (leavingChild) {
        bootstrap.Tooltip.getInstance(leftChildTooltipEl)?.hide();
      }
    }

    const cursorMovedOutsideParent = !event.relatedTarget || !parentEl.contains(event.relatedTarget);
    if (cursorMovedOutsideParent) {
      parentTooltip.hide();
      parentEl.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((el) => {
        if (el !== parentEl) bootstrap.Tooltip.getInstance(el)?.hide();
      });
      return;
    }

    const leftChildTooltip = this.nestedChildTooltipElement(event.target, parentEl);
    const enteredChildTooltip = event.relatedTarget
      && this.nestedChildTooltipElement(event.relatedTarget, parentEl);
    const enteredNonParentChild = enteredChildTooltip && enteredChildTooltip !== parentEl;
    const returnedToParentArea = leftChildTooltip && leftChildTooltip !== parentEl && !enteredNonParentChild;
    if (returnedToParentArea) {
      parentTooltip.show();
    }
  }

  // Reveals the fuzzy suggestions list on fuzzy-search-button click; empty-suggestions handling is managed in the HTML view.
  showFuzzySuggestions(event) {
    event.preventDefault();

    if (this.hasFuzzyCtaButtonTarget) {
      this.fuzzyCtaButtonTarget.classList.add('d-none');
      this.fuzzyCtaButtonTarget.setAttribute('aria-expanded', 'true');
    }

    if (this.hasFuzzySuggestionsListTarget) {
      this.fuzzySuggestionsListTarget.classList.remove('d-none');
    }
  }

  expandFuzzySuggestions(event) {
    event.preventDefault();

    this.element.querySelectorAll('.did-you-mean-row--collapsed').forEach((row) => {
      row.classList.remove('d-none');
    });

    if (this.hasFuzzyExpandButtonTarget) {
      this.fuzzyExpandButtonTarget.classList.add('d-none');
      this.fuzzyExpandButtonTarget.setAttribute('aria-expanded', 'true');
    }
  }

  // Independent of fuzzy suggestions: hide beside-scan slot, show below-list slot.
  showSubscriptionRecommendationBelowFuzzyList() {
    if (this.hasSubscriptionRecommendationBesideFuzzyCtaTarget) {
      this.subscriptionRecommendationBesideFuzzyCtaTarget.classList.add('d-none');
    }

    if (this.hasSubscriptionRecommendationBelowFuzzyListTarget) {
      this.subscriptionRecommendationBelowFuzzyListTarget.classList.remove('d-none');
    }
  }

  // Toggles ENG (navy) vs OTT (burgundy) styling on all "Does my library subscribe?" buttons.
  syncSubscriptionRecommendationButtonTheme(isEng = false) {
    this.element.querySelectorAll(".lexiqamus-btn-subscription-recommendation").forEach((btn) => {
      btn.classList.toggle("lexiqamus-btn-subscription-recommendation--eng", !!isEng)
    })
  }

  // Support URL hash for deep linking
  // TODO: This code AI generated code; it may not be needed. Remove this method once confirmed.
  // setupUrlHashSupport() {
  //   // Wait for DOM to be ready
  //   if (document.readyState === 'loading') {
  //     document.addEventListener('DOMContentLoaded', () => this.activateTabFromHash());
  //   } else {
  //     this.activateTabFromHash();
  //   }
  // }

  // TODO: This code AI generated code; it may not be needed. Remove this method once confirmed.
  // activateTabFromHash() {
  //   const anchor = window.location.hash.substring(1);
  //   if (anchor) {
  //     const tabTrigger = document.querySelector(`button[data-bs-target="#${anchor}"]`);
  //     if (tabTrigger) {
  //       const tab = new bootstrap.Tab(tabTrigger);
  //       tab.show();
  //     }
  //   }
  // }

  // TODO: This code AI generated code; it may not be needed. Remove this method once confirmed.
  // showFirstTableByDefault() {
  //   // For each pane, ensure its main table is visible initially
  //   document.querySelectorAll('.js-search-by-spelling-container').forEach(container => {
  //     const mainTable = container.querySelector('.js-search-results-table[id$="-main"]');
  //     if (mainTable) {
  //       mainTable.classList.remove('d-none');
  //     }
  //   });
  // }

  // Word Box Copy Functionality
  async copyWordBoxValue(event) {
    event.preventDefault();
    event.stopPropagation();

    const copyButton = event.currentTarget;
    const value = copyButton.dataset.copyValue?.trim();
    if (!value) return;

    try {
      await this.copyToClipboard(value);
      this.showWordBoxCopySuccess(copyButton);
    } catch (_error) {
      // Preserve button state when clipboard access is denied or unavailable.
    }
  }

  // Show visual feedback for word box copy (Feather check icon, same pattern as showCopySuccess).
  showWordBoxCopySuccess(copyButton) {
    if (copyButton.dataset.copyResetTimeoutId) {
      clearTimeout(Number(copyButton.dataset.copyResetTimeoutId));
    }

    if (!copyButton.dataset.originalCopyIconHtml) {
      copyButton.dataset.originalCopyIconHtml = copyButton.innerHTML;
    }

    copyButton.classList.add("is-copied");
    copyButton.setAttribute("aria-label", this.copiedValue);
    copyButton.setAttribute("title", this.copiedValue);

    if (typeof feather !== "undefined") {
      copyButton.innerHTML = feather.icons.check.toSvg({
        width: 11,
        height: 11,
        "aria-hidden": "true"
      });
    }

    const timeoutId = setTimeout(() => {
      copyButton.innerHTML = copyButton.dataset.originalCopyIconHtml;
      copyButton.classList.remove("is-copied");
      copyButton.setAttribute("aria-label", copyButton.dataset.copyWordLabel);
      copyButton.setAttribute("title", copyButton.dataset.copyWordLabel);
      delete copyButton.dataset.copyResetTimeoutId;
    }, 1500);

    copyButton.dataset.copyResetTimeoutId = String(timeoutId);
  }

  // Citation Copy Functionality
  copyCitation(event) {
    const copyButton = event.currentTarget;
    const citationContainer = copyButton.closest('.js-citation-format');
    const citationText = citationContainer.querySelector('.js-citation-text');

    if (!citationText) return;

    const textToCopy = citationText.textContent.trim();

    // Copy to clipboard using standard pattern
    this.copyToClipboard(textToCopy);
    this.showCopySuccess(copyButton);
  }

  setupCitationModal() {
    // Populate citation modal dynamically when opened
    const modalElement = document.getElementById('citation-modal-global');
    if (modalElement) {
      modalElement.addEventListener('show.bs.modal', (event) => {
        this.setCiteButtonActive(event.relatedTarget);
        this.populateCitationModal(event);
      });
      modalElement.addEventListener('hidden.bs.modal', () => {
        this.clearCiteButtonActive();
      });
    }
  }

  setCiteButtonActive(button) {
    this.clearCiteButtonActive();
    if (!button) return;
    button.classList.add('is-active');
    this.activeCiteButton = button;
  }

  clearCiteButtonActive() {
    if (this.activeCiteButton) {
      this.activeCiteButton.classList.remove('is-active');
      this.activeCiteButton = null;
    }
  }

  populateCitationModal(event) {
    const button = event.relatedTarget;
    if (!button) {
      return;
    }

    // ---------------------------------------------------------------------------
    // Backward-compatible: if the button still carries the legacy inline JSON
    // (data-citation-data), use it directly without an AJAX call.
    // ---------------------------------------------------------------------------
    if (button.dataset.citationData) {
      try {
        const citationData = JSON.parse(button.dataset.citationData);
        this.renderCitationData(citationData);
      } catch (e) {
        console.error('Error parsing citation data:', e);
      }
      return;
    }

    // ---------------------------------------------------------------------------
    // New path: fetch citation data via jQuery AJAX using minimal identifiers.
    // ---------------------------------------------------------------------------
    const dictionaryId = button.dataset.dictionaryId;
    const pageNumber   = button.dataset.pageNumber;

    if (!dictionaryId) {
      return;
    }

    // Show a brief loading state in the modal body
    const bookTitleEl = document.getElementById('citation-book-title');
    if (bookTitleEl) bookTitleEl.textContent = '…';

    // Determine citation URL based on current page (same pattern as logClickUrl / logSearchUrl)
    const currentPath = window.location.pathname;
    const isLexiqamusPath = currentPath.includes('/lexiqamus');
    const citationUrl = isLexiqamusPath
      ? '/lexiqamus/search_results/citation_data'
      : '/search_output_modified/citation_data';

    $.ajax({
      url: citationUrl,
      type: 'GET',
      dataType: 'json',
      data: {
        dictionary_id: dictionaryId,
        page_number: pageNumber
      },
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      }
    }).done((citationData) => {
      this.renderCitationData(citationData);
    }).fail((xhr, status, error) => {
      console.error('Failed to fetch citation data:', error, xhr.status, xhr.responseText);
    });
  }

  // Shared helper that populates the global citation modal fields.
  // Used by both the legacy inline-JSON path and the new AJAX path.
  renderCitationData(citationData) {
    // Populate source information fields
    const bookTitleEl = document.getElementById('citation-book-title');
    const authorsEl   = document.getElementById('citation-authors');
    const yearEl      = document.getElementById('citation-year');
    const volumeEl    = document.getElementById('citation-volume');
    const pageEl      = document.getElementById('citation-page');

    if (bookTitleEl) bookTitleEl.textContent = citationData.dictionary_title || '';
    if (authorsEl) authorsEl.textContent = citationData.dictionary_authors && citationData.dictionary_authors.length > 0
      ? (Array.isArray(citationData.dictionary_authors) ? citationData.dictionary_authors.join(', ') : citationData.dictionary_authors)
      : '';
    if (yearEl) yearEl.textContent = citationData.dictionary_year || '';
    if (volumeEl) volumeEl.textContent = citationData.dictionary_volume || '';
    if (pageEl) pageEl.textContent = citationData.page_number || '';

    // Populate citation format text blocks
    const apaEl     = document.getElementById('citation-apa-text');
    const chicagoEl = document.getElementById('citation-chicago-text');
    const harvardEl = document.getElementById('citation-harvard-text');

    if (apaEl) apaEl.innerHTML = citationData.apa_citation || '';
    if (chicagoEl) chicagoEl.innerHTML = citationData.chicago_citation || '';
    if (harvardEl) harvardEl.innerHTML = citationData.harvard_citation || '';

    // Re-initialize feather icons
    if (typeof feather !== 'undefined') {
      feather.replace();
    }
  }

  copyToClipboard(content) {
    return navigator.clipboard.writeText(content);
  }

  showCopySuccess(copyButton) {
    if (!copyButton.dataset.originalCopyHtml) {
      copyButton.dataset.originalCopyHtml = copyButton.innerHTML;
    }

    const copiedText = this.copiedValue || 'Copied!';

    copyButton.innerHTML = `<i data-feather="check" style="width: 14px; height: 14px;"></i> ${copiedText}`;
    copyButton.classList.add('btn-success');
    copyButton.classList.remove('btn-outline-primary');

    if (typeof feather !== 'undefined') {
      feather.replace();
    }

    setTimeout(() => {
      copyButton.innerHTML = copyButton.dataset.originalCopyHtml;
      copyButton.classList.remove('btn-success');
      copyButton.classList.add('btn-outline-primary');

      if (typeof feather !== 'undefined') {
        feather.replace();
      }
    }, 2000);
  }

  // Log click events on LQ search results
  logClicksOnLqSearchResults(event) {
    const currentPath = window.location.pathname;
    const isLexiqamusPath = currentPath.includes('/lexiqamus');
    
    // Restrict logging to LexiQamus path only - early return if not LexiQamus
    if (!isLexiqamusPath) {
      return;
    }

    // Use only LexiQamus endpoint
    const logClickUrl = '/lexiqamus/search_results/log_click';
    
    // Make AJAX call to log_click - backend will read searched_text and found_count from session
    $.ajax({
      url: logClickUrl,
      type: 'POST',
      data: {},
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      }
    }).done(function(data, status, xhr) {
      console.log('Tab click logged successfully:', status);
    }).fail(function(xhr, status, error) {
      console.warn('Failed to log view click:', error, xhr.status, xhr.responseText);
    });
  }

  // Log search events on LQ search results
  logSearchesOnLqSearchResults(event) {
    const currentPath     = window.location.pathname;
    const isLexiqamusPath = currentPath.includes('/lexiqamus');

    // Restrict logging to LexiQamus path only - early return if not LexiQamus
    if (!isLexiqamusPath) {
      return;
    }

    // Old conditional URL logic (kept for reference, not used)
    // const logSearchUrl = isLexiqamusPath 
    //   ? '/lexiqamus/search_results/log_search'
    //   : '/search_output_modified/log_search';

    // Use only LexiQamus endpoint
    const logSearchUrl = '/lexiqamus/search_results/log_search';
    
    // Check if this is called from _results_tabs.html.erb (tab buttons) or main tabs
    const clickedButton = event.currentTarget;
    const buttonSource = clickedButton.getAttribute('data-source');
    const isFromResultsTabs = buttonSource === 'word-search-results' || buttonSource === 'tab-search-results';
    const isFromMainTabs = buttonSource === 'exact-match' || buttonSource === 'sounds-like';
    
    let requestData = {};
    
    // If called from _results_tabs.html.erb (Ottoman/Latin value tabs), extract button label and table rows count
    if (isFromResultsTabs) {
      // Get the clicked button's label
      const buttonLabel = clickedButton?.textContent?.trim() || '';
      
      // Get the table ID from button's href (e.g., "#table-agac-0-exact_match")
      const buttonHref = clickedButton.getAttribute('href') || '';
      const targetTableId = buttonHref.startsWith('#') ? buttonHref.substring(1) : buttonHref;

      // Find the table container that will be shown when this tab is clicked
      const tableContainer = document.getElementById(targetTableId);
      
      // Count rows in the table that appears for this tab
      let tableRowsCount = 0;
      if (tableContainer) {
        // Find the table inside the container
        const table = tableContainer.querySelector('table');
        if (table) {
          // Count rows in tbody (excluding header row)
          const tbody = table.querySelector('tbody');
          if (tbody) {
            const rows = tbody.querySelectorAll('tr');
            tableRowsCount = rows.length;
          }
        }
      }
      
      // Pass button label as searched_text and table rows count as found_count
      if (buttonLabel) {
        requestData.searched_text = buttonLabel;
        requestData.found_count   = tableRowsCount;
      }
    }
    // If called from main tabs (Exact Match, Partial Match, Sounds Like)
    else if (isFromMainTabs) {
      // Get searched_text from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const searchedText = urlParams.get('searched_text') || '';
      
      // Get the tab pane that will be shown (from data-bs-target attribute)
      const tabTarget = clickedButton.getAttribute('data-bs-target'); // e.g., "#exact-match-tab"
      const tabPane   = document.querySelector(tabTarget);
      
      // Find the main table in the tab pane (id ends with "-main")
      let tableRowsCount = 0;
      if (tabPane) {
        const resultsContainer = tabPane.querySelector('.js-search-by-spelling-container');
        if (resultsContainer) {
          // Find the main table (id ends with "-main")
          const mainTable = resultsContainer.querySelector('.js-search-results-table[id$="-main"]');
          if (mainTable) {
            // Find the table element inside the container
            const table = mainTable.querySelector('table');
            if (table) {
              // Count rows in tbody
              const tbody = table.querySelector('tbody');
              if (tbody) {
                const rows = tbody.querySelectorAll('tr');
                tableRowsCount = rows.length;
              }
            }
          }
        }
      }
      
      // Pass searched_text from URL and table rows count as found_count
      if (searchedText) {
        requestData.searched_text = searchedText;
        requestData.found_count   = tableRowsCount;
      }
    }
    // Otherwise, backend will read from session (existing behavior)
    // Make AJAX call to log_search
    $.ajax({
      url: logSearchUrl,
      type: 'POST',
      data: requestData,
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      }
    }).done(function(data, status, xhr) {
      console.log('Search event logged successfully:', status);
    }).fail(function(xhr, status, error) {
      console.warn('Failed to log search event:', error, xhr.status, xhr.responseText);
    });
  }

  // Pre-search landing: lock page scroll via CSS class (innovations page only).
  lockLandingPageScroll() {
    if (!document.getElementById('lexiqamus-innovations-root')) return
    document.documentElement.classList.add('lexiqamus-landing-scroll-locked')
    document.body.classList.add('lexiqamus-landing-scroll-locked')
  }

  // Discover 2026 Innovations: unlock page scroll and mark first section active.
  enableLandingInnovationsScroll(event) {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return
    if (event.type === 'keydown') event.preventDefault()

    document.documentElement.classList.remove('lexiqamus-landing-scroll-locked')
    document.body.classList.remove('lexiqamus-landing-scroll-locked')
    this.isDiscoverInnovationsScrollEnable = true
    document.querySelector('#lexiqamus-innovations-root .lexiqamus-snap-section')?.classList.add('active')
  }

  // Landing wheel (HTML: wheel@window->search-output#landingWheel).
  landingWheel(event) {
    if (!this.isDiscoverInnovationsScrollEnable) return

    event.preventDefault()
    if (Math.abs(event.deltaY) < 4) return

    const active = document.querySelector('#lexiqamus-innovations-root .lexiqamus-snap-section.active')
    let arrow

    // Back at search — no active section; wheel down re-enters first innovations section.
    if (!active) {
      if (event.deltaY > 0) {
        const first = document.querySelector('#lexiqamus-innovations-root .lexiqamus-snap-section')
        first?.classList.add('active')
        document.getElementById('sectionBuildLQ')?.scrollIntoView({ behavior: 'smooth' })
      }
      return
    }

    if (event.deltaY > 0) {
      arrow = active.querySelector('.lexiqamus-section-nav-down[data-scroll-target]')
      arrow?.click()
    } else {
      arrow = active.querySelector('.lexiqamus-section-nav-up[data-scroll-target]')
      arrow?.click()
    }

    const targetId = arrow?.dataset.scrollTarget
    if (targetId === 'top') {
      active.classList.remove('active')
    } else if (targetId) {
      document.getElementById(targetId)?.classList.add('active')
      active.classList.remove('active')
    }
  }

  // Search field / dictionaries dropdown / word decoder panel: collapse landing to hide Discover + sections.
  collapseLandingSections(event) {
    if (!document.getElementById('lexiqamus-innovations-root')) return

    const parent = event.currentTarget.closest('.lexiqamus-home-viewport')
    parent?.classList.add('lexiqamus-landing-collapsed')
    document.querySelector('.js-lexiqamus-innovations-wrap')?.classList.add('lexiqamus-landing-collapsed')
  }
}
